import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert } from 'antd';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  AggregatesDirective,
  AggregateDirective,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  Inject,
  Aggregate,
} from '@syncfusion/ej2-react-grids';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '@/store';
import {
  getDashboardMetric23,
  type DashboardMetric23AuditorAssignment,
} from '@/services/dashboardService';

const GridWrapper = styled.div`
  .e-grid *,
  .e-grid *::before,
  .e-grid *::after {
    box-sizing: border-box;
  }

  .e-grid .e-summaryrow .e-summarycell,
  .e-grid .e-summaryrow .e-summarycontent,
  .e-grid .e-summarycell {
    padding: 12px 16px !important;
    border-color: #e5e7eb !important;
    vertical-align: middle;
  }

  .e-grid .e-summaryrow .e-summarycell {
    font-size: 14px;
    color: #1f2937;
  }
`;

export type EX1AuditorAssignmentDashboardGridProps = {
  title?: string;
  minHeight?: number;
};

type FlatRow = {
  id: string;
  auditorName: string;
  [monthKey: string]: string | number;
};

export const EX1AuditorAssignmentDashboardGrid: React.FC<
  EX1AuditorAssignmentDashboardGridProps
> = ({
  title = 'Auditor Assignment by Month',
  minHeight = 220,
}) => {
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [rows, setRows] = useState<DashboardMetric23AuditorAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRows([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric23(annualReportId);
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRows([]);
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Metric 23.',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const months = useMemo(() => {
    if (rows.length === 0) return [];
    const first = rows[0];
    if (!first.monthlyHours || first.monthlyHours.length === 0) return [];
    return first.monthlyHours.map(m => ({
      month: m.month,
      monthName: m.monthName,
      field: `month_${m.month}`,
    }));
  }, [rows]);

  const dataSource: FlatRow[] = useMemo(() => {
    return rows.map(auditor => {
      const flat: FlatRow = {
        id: auditor.auditorId,
        auditorName: auditor.auditorName,
      };
      for (const mh of auditor.monthlyHours ?? []) {
        flat[`month_${mh.month}`] = mh.assignedWeeks;
      }
      return flat;
    });
  }, [rows]);

  const sumField = (result: any[], field: string) => {
    const nums = (result || [])
      .map(r => r?.[field])
      .filter(v => typeof v === 'number' && !Number.isNaN(v));
    return nums.reduce((a, b) => a + b, 0);
  };

  const labelAggregate = (_props: any): string => 'Totals';

  const footerLabelTemplate = (_props: any) => (
    <div style={{ fontWeight: 600 }}>Totals</div>
  );

  const footerSumTemplate = (props: any) => (
    <div style={{ fontWeight: 600 }}>{props?.Custom ?? 0}</div>
  );

  return (
    <Card title={title}>
      {loading ? (
        <div
          style={{
            minHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : errorMessage ? (
        <div style={{ padding: 16 }}>
          <Alert
            type='error'
            message='Dashboard Metric 23 Error'
            description={errorMessage}
            showIcon
          />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <GridWrapper>
            <GridComponent
              dataSource={dataSource}
              height='auto'
              width='100%'
              allowSorting={false}
              allowFiltering={false}
              enableHover={true}
              rowHeight={44}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='auditorName'
                  headerText='Auditor'
                  width={180}
                  textAlign='Left'
                  clipMode='EllipsisWithTooltip'
                />
                {months.map(m => (
                  <ColumnDirective
                    key={m.field}
                    field={m.field}
                    headerText={m.monthName.substring(0, 3)}
                    width={70}
                    textAlign='Center'
                  />
                ))}
              </ColumnsDirective>

              <AggregatesDirective>
                <AggregateDirective>
                  <AggregateColumnsDirective>
                    <AggregateColumnDirective
                      field='auditorName'
                      type='Custom'
                      customAggregate={labelAggregate}
                      footerTemplate={footerLabelTemplate}
                    />
                    {months.map(m => (
                      <AggregateColumnDirective
                        key={m.field}
                        field={m.field}
                        type='Custom'
                        customAggregate={(props: any) =>
                          sumField(props?.result || [], m.field)
                        }
                        footerTemplate={footerSumTemplate}
                      />
                    ))}
                  </AggregateColumnsDirective>
                </AggregateDirective>
              </AggregatesDirective>

              <Inject services={[Aggregate]} />
            </GridComponent>
          </GridWrapper>
        </div>
      )}
    </Card>
  );
};

export default EX1AuditorAssignmentDashboardGrid;
